/**
 * Duration clock — pure per-field logic for a 24-hour HH:MM duration input
 * with two clickable segments (hours / minutes). No AM/PM, hours-first.
 *
 *   Hours 00–23: first digit 3–9 commits as "0X" and advances to minutes
 *     ("6" → 06h). A leading "2" waits; second digit 0–3 → 20–23, otherwise
 *     the "2" commits as "02" and the digit overflows into minutes
 *     ("26" → 02h, then 6 → 06m).
 *   Minutes 00–59: first digit 6–9 commits as "0X" ("6" → 06, "60" → "06").
 *
 * The emitted value is the 4-char "HHMM" string the rest of the app uses
 * (parseDuration / BE), e.g. {hh:"02", mm:"06"} → "0206".
 */

export interface DurationParts {
  hh: string;
  mm: string;
}

export interface HoursResult {
  hh: string;
  /** Move focus to the minutes field after this digit. */
  advance: boolean;
  /** A digit that overflowed out of hours and should seed minutes (or null). */
  overflow: string | null;
}

export interface MinutesResult {
  mm: string;
  /** Minutes are now full (2 digits). */
  full: boolean;
}

/** Apply a typed digit to the hours segment. `hh` is the current 0–2 chars. */
export function typeHours(hh: string, key: string): HoursResult {
  if (!/^[0-9]$/.test(key)) return { hh, advance: false, overflow: null };

  if (hh.length === 0) {
    return key <= "2"
      ? { hh: key, advance: false, overflow: null } // tens digit — wait
      : { hh: "0" + key, advance: true, overflow: null }; // 3–9 → 0X, done
  }
  if (hh.length === 1) {
    if (hh === "2") {
      return key <= "3"
        ? { hh: "2" + key, advance: true, overflow: null } // 20–23
        : { hh: "02", advance: true, overflow: key }; // 24–29 → 02h, digit → minutes
    }
    return { hh: hh + key, advance: true, overflow: null }; // 00–19
  }
  // hours already full — the digit overflows into minutes
  return { hh, advance: true, overflow: key };
}

/** Apply a typed digit to the minutes segment. `mm` is the current 0–2 chars. */
export function typeMinutes(mm: string, key: string): MinutesResult {
  if (!/^[0-9]$/.test(key)) return { mm, full: mm.length >= 2 };

  if (mm.length === 0) {
    return key <= "5"
      ? { mm: key, full: false }
      : { mm: "0" + key, full: true }; // 6–9 → 0X
  }
  if (mm.length === 1) return { mm: mm + key, full: true };
  return { mm, full: true }; // already full — ignore
}

/** Pad a 0–2 char segment to "00"–"23"/"59" for display/value. */
export function padSegment(seg: string): string {
  return seg.padStart(2, "0");
}

/** Emit value: "" when nothing entered, else the 4-char "HHMM" string. */
export function partsToDigits(parts: DurationParts): string {
  if (!parts.hh && !parts.mm) return "";
  return padSegment(parts.hh) + padSegment(parts.mm);
}

/** Build parts from an incoming "HHMM" value (e.g. parseDuration(min).fullStr). */
export function partsFromValue(value: string | null | undefined): DurationParts {
  const digits = (value ?? "").replace(/[^0-9]/g, "");
  if (!digits) return { hh: "", mm: "" };
  const four = digits.padStart(4, "0").slice(-4);
  return { hh: four.slice(0, 2), mm: four.slice(2, 4) };
}
